import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  Database,
  FilePlus2,
  FileText,
  HardDrive,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getApiErrorMessage, localAiApi } from '../api';
import { useAuth } from '../store/AuthContext';
import PageIntro from '../components/ui/PageIntro';

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

function statusLabel(status) {
  return {
    pending: uiText("В очереди"),
    indexing: uiText("Индексируется"),
    ready: uiText("Готов"),
    failed: uiText("Ошибка"),
    cancelled: uiText("Отменено"),
  }[status] || status;
}

function renderInlineMarkdown(text, keyPrefix) {
  return String(text || '').split(/(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`)/g).filter(Boolean).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

function AiMessageContent({ content }) {
  const lines = String(content || '').replace(/\r\n/g, '\n').split('\n');
  const nodes = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    const listKey = `list-${nodes.length}`;
    nodes.push(
      <ul key={listKey}>
        {listItems.map((item, index) => (
          <li key={`${listKey}-${index}`}>{renderInlineMarkdown(item, `${listKey}-${index}`)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    const bullet = line.match(/^[-•]\s+(.+)$/);
    if (bullet) {
      listItems.push(bullet[1]);
      return;
    }
    flushList();
    if (!line) {
      nodes.push(<span key={`space-${index}`} className="local-ai-message-space" aria-hidden="true" />);
      return;
    }
    const heading = line.match(/^#{1,4}\s+(.+)$/);
    nodes.push(
      heading
        ? <h3 key={`line-${index}`}>{renderInlineMarkdown(heading[1], `line-${index}`)}</h3>
        : <p key={`line-${index}`}>{renderInlineMarkdown(line, `line-${index}`)}</p>
    );
  });
  flushList();
  return <div className="local-ai-message-rich">{nodes}</div>;
}

export default function LocalAiAssistantPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [scopeMode, setScopeMode] = useState(() => localStorage.getItem(`local-ai-scope:${user?.id || 'anonymous'}`) || 'all');
  const messageEndRef = useRef(null);
  const selectedConversationIdRef = useRef(null);
  const pollingRef = useRef(null);
  const conversationStorageKey = `local-ai-current-conversation:${user?.id || 'anonymous'}`;
  const scopedDocumentIds = useMemo(() => {
    if (scopeMode === 'all') return null;
    return documents
      .filter((item) => item.status === 'ready')
      .filter((item) => scopeMode === 'personal' ? item.source_kind === 'upload' : item.source_kind !== 'upload')
      .map((item) => item.id);
  }, [documents, scopeMode]);
  const scopeLabel = scopeMode === 'personal' ? uiText("Только личные документы") : scopeMode === 'shared' ? uiText("Только общая база") : uiText("Все доступные документы");

  useEffect(() => {
    localStorage.setItem(`local-ai-scope:${user?.id || 'anonymous'}`, scopeMode);
  }, [scopeMode, user?.id]);

  const loadOverview = async ({ quiet = false } = {}) => {
    if (!quiet) setError('');
    const [statusResult, conversationsResult, documentsResult] = await Promise.allSettled([
      localAiApi.status(),
      localAiApi.conversations(),
      localAiApi.documents(),
    ]);
    if (statusResult.status === 'fulfilled') setStatus(statusResult.value.data);
    else if (!quiet) setError(getApiErrorMessage(statusResult.reason));
    const conversationRows = conversationsResult.status === 'fulfilled' ? (conversationsResult.value.data || []) : null;
    if (conversationRows) setConversations(conversationRows);
    if (documentsResult.status === 'fulfilled') setDocuments(documentsResult.value.data || []);
    return { conversations: conversationRows || [] };
  };

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages, sending]);

  const pollForAnswer = async (conversationId, userMessageId) => {
    const pollKey = `${conversationId}:${userMessageId}`;
    if (pollingRef.current === pollKey) return;
    pollingRef.current = pollKey;
    setSending(true);
    try {
      for (let attempt = 0; attempt < 360; attempt += 1) {
        if (pollingRef.current !== pollKey) return;
        const response = await localAiApi.getConversation(conversationId);
        const nextConversation = response.data;
        if (selectedConversationIdRef.current === conversationId) {
          setConversation(nextConversation);
        }
        const completed = (nextConversation.messages || []).some(
          (item) => (
            Number(item.reply_to_message_id) === Number(userMessageId)
            || (!item.reply_to_message_id && Number(item.id) > Number(userMessageId))
          ) && ['assistant', 'error'].includes(item.role)
        );
        if (completed) {
          await loadOverview({ quiet: true });
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
      }
      throw new Error(uiText("Ответ всё ещё формируется. Диалог сохранён — результат появится здесь автоматически."));
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      if (pollingRef.current === pollKey) {
        pollingRef.current = null;
        setSending(false);
      }
    }
  };

  const openConversation = async (id) => {
    setError('');
    try {
      const response = await localAiApi.getConversation(id);
      if (selectedConversationIdRef.current !== id) {
        pollingRef.current = null;
        setSending(false);
      }
      selectedConversationIdRef.current = id;
      localStorage.setItem(conversationStorageKey, String(id));
      setConversation(response.data);
      const messages = response.data?.messages || [];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === 'user') {
        pollForAnswer(id, lastMessage.id);
      }
    } catch (e) {
      localStorage.removeItem(conversationStorageKey);
      setError(getApiErrorMessage(e));
    }
  };

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      const overview = await loadOverview();
      if (!active) return;
      const storedId = Number(localStorage.getItem(conversationStorageKey));
      const rows = overview.conversations || [];
      const selected = rows.find((item) => item.id === storedId) || rows[0];
      if (selected) await openConversation(selected.id);
    };
    bootstrap();
    const timer = window.setInterval(() => loadOverview({ quiet: true }), 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [conversationStorageKey]);

  const createConversation = async () => {
    setError('');
    try {
      const response = await localAiApi.createConversation();
      selectedConversationIdRef.current = response.data.id;
      localStorage.setItem(conversationStorageKey, String(response.data.id));
      setConversation(response.data);
      await loadOverview({ quiet: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = message.trim();
    if (!text || sending) return;
    let current = conversation;
    if (!current) {
      try {
        const created = await localAiApi.createConversation();
        current = created.data;
        selectedConversationIdRef.current = current.id;
        localStorage.setItem(conversationStorageKey, String(current.id));
        setConversation(current);
      } catch (e) {
        setError(getApiErrorMessage(e));
        return;
      }
    }
    const optimistic = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      sources: [],
    };
    setConversation((prev) => ({ ...current, ...(prev || {}), messages: [...(prev?.messages || current.messages || []), optimistic] }));
    setMessage('');
    setSending(true);
    setError('');
    try {
      const response = await localAiApi.askAsync(current.id, text, { documentIds: scopedDocumentIds });
      const refreshed = await localAiApi.getConversation(current.id);
      setConversation(refreshed.data);
      await loadOverview({ quiet: true });
      await pollForAnswer(current.id, response.data.user_message.id);
      return response.data;
    } catch (e) {
      setError(getApiErrorMessage(e));
      setSending(false);
      const refreshed = await localAiApi.getConversation(current.id).catch(() => null);
      if (refreshed) setConversation(refreshed.data);
    }
    return null;
  };

  const uploadFiles = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    setUploading(true);
    setUploadProgress(null);
    setError('');
    try {
      for (const file of selected) {
        setUploadProgress({ name: file.name, percent: 0 });
        await localAiApi.uploadDocument(file, (event) => {
          const total = event.total || file.size || 1;
          setUploadProgress({
            name: file.name,
            percent: Math.min(100, Math.round((event.loaded / total) * 100)),
          });
        });
        await loadOverview({ quiet: true });
      }
      await loadOverview({ quiet: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const syncAudits = async () => {
    setSyncing(true);
    setError('');
    try {
      await localAiApi.indexAudits();
      await loadOverview({ quiet: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setSyncing(false);
    }
  };

  const archiveConversation = async (id) => {
    try {
      await localAiApi.archiveConversation(id);
      const wasCurrent = conversation?.id === id;
      if (wasCurrent) {
        pollingRef.current = null;
        selectedConversationIdRef.current = null;
        localStorage.removeItem(conversationStorageKey);
        setConversation(null);
        setSending(false);
      }
      const overview = await loadOverview({ quiet: true });
      if (wasCurrent && overview.conversations[0]) {
        await openConversation(overview.conversations[0].id);
      }
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const deleteDocument = async (id) => {
    try {
      await localAiApi.deleteDocument(id);
      await loadOverview({ quiet: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const cancelDocument = async (id) => {
    try {
      await localAiApi.cancelDocument(id);
      await loadOverview({ quiet: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const readyDocuments = useMemo(() => documents.filter((item) => item.status === 'ready').length, [documents]);
  const modelReady = status?.runtime?.ok && status?.runtime?.chat_model_ready && status?.runtime?.embedding_model_ready;

  return (
    <div className="analytics-page local-ai-page">
      <PageIntro
        icon={Bot}
        title={uiText("Локальный ИИ-ассистент аудитора")}
        description={uiText("Анализирует доступные вам аудиты и личные загруженные документы. Все вычисления выполняются на этом сервере.")}
        aside={(
          <div className={`local-ai-runtime ${modelReady ? 'is-ready' : 'is-waiting'}`}>
            <ShieldCheck size={18} />
            <div>
              <strong>{modelReady ? uiText("Локальный контур готов") : uiText("Модель подготавливается")}</strong>
              <span>{uiText("Без облачных API · данные не покидают сервер")}</span>
            </div>
          </div>
        )}
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="local-ai-stats">
        <div><HardDrive size={18} /><span><strong>{readyDocuments}</strong>{uiText(" документов готовы")}</span></div>
        <div><Database size={18} /><span><strong>{status?.documents?.processing || 0}</strong>{uiText(" обрабатываются")}</span></div>
        <div><Bot size={18} /><span>{uiText("Модель ")}<strong>{status?.chat_model || 'Qwen3'}</strong></span></div>
      </div>

      <div className="local-ai-layout">
        <aside className="local-ai-sidebar">
          <button type="button" className="btn btn-primary local-ai-new-chat" onClick={createConversation}>
            <MessageSquarePlus size={17} />{uiText(" Новый диалог")}</button>
          <div className="local-ai-sidebar-title">{uiText("История диалогов")}</div>
          <div className="local-ai-conversations">
            {conversations.map((item) => (
              <div key={item.id} className={`local-ai-conversation ${conversation?.id === item.id ? 'is-active' : ''}`}>
                <button type="button" onClick={() => openConversation(item.id)}>
                  <strong>{item.title}</strong>
                  <span>{item.messages_count}{uiText(" сообщ. · ")}{formatDate(item.updated_at)}</span>
                </button>
                <button type="button" className="local-ai-icon-button" title={uiText("Убрать диалог в архив")} onClick={() => archiveConversation(item.id)}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {!conversations.length ? <div className="local-ai-empty-small">{uiText("Пока нет диалогов")}</div> : null}
          </div>
        </aside>

        <main className="local-ai-chat">
          <div className="local-ai-chat-head">
            <div>
              <strong>{conversation?.title || uiText("Новый диалог")}</strong>
              <span>{uiText("Полная история сохраняется автоматически")}</span>
            </div>
            <span className="workspace-badge">{uiText("Локально")}</span>
          </div>
          <div className="local-ai-messages">
            {!conversation?.messages?.length ? (
              <div className="local-ai-welcome">
                <Bot size={34} />
                <h2>{uiText("Что нужно проанализировать?")}</h2>
                <p>{uiText("Можно спросить о замечаниях, сроках, ПКПД, документах аудита или загрузить дополнительные таблицы и отчёты.")}</p>
                <div className="local-ai-suggestions">
                  <button type="button" onClick={() => setMessage(uiText("Какие открытые замечания требуют внимания и почему?"))}>{uiText("Открытые замечания")}</button>
                  <button type="button" onClick={() => setMessage(uiText("Сравни аудиты и выдели повторяющиеся несоответствия."))}>{uiText("Повторяющиеся несоответствия")}</button>
                  <button type="button" onClick={() => setMessage(uiText("Составь краткую управленческую сводку по доступным аудитам."))}>{uiText("Управленческая сводка")}</button>
                </div>
              </div>
            ) : null}
            {(conversation?.messages || []).map((item) => (
              <article key={item.id} className={`local-ai-message is-${item.role}`}>
                <div className="local-ai-message-role">
                  {item.role === 'assistant' ? uiText("ИИ-ассистент") : item.role === 'error' ? uiText("Система") : uiText("Вы")}
                </div>
                <div className="local-ai-message-content">
                  {['assistant', 'error'].includes(item.role) ? <AiMessageContent content={item.content} /> : item.content}
                </div>
                {item.sources?.length ? (
                  <div className="local-ai-sources">
                    <strong>{uiText("Использованные источники")}</strong>
                    {item.sources.map((source) => (
                      source.audit_id ? (
                        <Link key={`${item.id}-${source.number}`} to={`/audits/${source.audit_id}`}>
                          [{source.number}] {source.title}{source.location?.page ? uiText(" · стр. {v0}", {v0: (source.location.page)}) : ''}{source.location?.sheet ? uiText(" · лист {v0}", {v0: (source.location.sheet)}) : ''}
                        </Link>
                      ) : (
                        <span key={`${item.id}-${source.number}`}>[{source.number}] {source.title}</span>
                      )
                    ))}
                  </div>
                ) : item.role === 'assistant' ? (
                  <div className="local-ai-no-sources"><AlertTriangle size={14} />{uiText(" Ответ не подтвержден документами базы знаний. Проверьте вывод перед использованием.")}</div>
                ) : null}
              </article>
            ))}
            {sending ? (
              <div className="local-ai-thinking"><Loader2 className="spin" size={18} />{uiText(" Локальная модель анализирует документы…")}</div>
            ) : null}
            <div ref={messageEndRef} />
          </div>
          <form className="local-ai-composer" onSubmit={sendMessage}>
            <div className="local-ai-composer-scope">
              <ShieldCheck size={14} />
              <span>{scopeLabel}{scopedDocumentIds ? uiText(" · {v0} док.", {v0: (scopedDocumentIds.length)}) : ''}</span>
            </div>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage(event);
                }
              }}
              placeholder={uiText("Задайте вопрос по аудитам и документам…")}
              rows={3}
              disabled={sending || !modelReady}
            />
            <button type="submit" className="btn btn-primary" disabled={!message.trim() || sending || !modelReady}>
              <Send size={17} />{uiText(" Отправить")}</button>
          </form>
        </main>

        <aside className="local-ai-documents">
          <div className="local-ai-documents-head">
            <div>
              <strong>{uiText("База знаний")}</strong>
              <span>{uiText("Автоматически обновляется каждую минуту")}</span>
            </div>
            <button type="button" className="local-ai-icon-button" title={uiText("Обновить")} onClick={() => loadOverview()}>
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="local-ai-scope-filter" role="group" aria-label={uiText("Область поиска ИИ")}>
            <button type="button" className={scopeMode === 'all' ? 'is-active' : ''} onClick={() => setScopeMode('all')}>{uiText("Все")}</button>
            <button type="button" className={scopeMode === 'shared' ? 'is-active' : ''} onClick={() => setScopeMode('shared')}>{uiText("Общие")}</button>
            <button type="button" className={scopeMode === 'personal' ? 'is-active' : ''} onClick={() => setScopeMode('personal')}>{uiText("Личные")}</button>
          </div>
          <label className="local-ai-upload">
            <FilePlus2 size={22} />
            <strong>{uploading ? uiText("Загрузка…") : uiText("Добавить документы")}</strong>
            <span>PDF, DOCX, XLSX, CSV, TXT</span>
            {uploadProgress ? (
              <div className="local-ai-upload-progress" aria-label={uiText("Загружено {v0}%", {v0: (uploadProgress.percent)})}>
                <div><span style={{ width: `${uploadProgress.percent}%` }} /></div>
                <small>{uploadProgress.name} · {uploadProgress.percent}%</small>
              </div>
            ) : null}
            <input type="file" multiple hidden disabled={uploading} accept=".pdf,.docx,.xlsx,.csv,.txt" onChange={(event) => {
              uploadFiles(event.target.files);
              event.target.value = '';
            }} />
          </label>
          <button type="button" className="btn btn-outline local-ai-sync" onClick={syncAudits} disabled={syncing || !status?.runtime?.embedding_model_ready}>
            {syncing ? <Loader2 className="spin" size={16} /> : <Database size={16} />}{uiText("Синхронизировать сейчас")}</button>
          <div className="local-ai-document-list">
            {documents.map((item) => (
              <div key={item.id} className="local-ai-document">
                <FileText size={17} />
                <div>
                  <div className="local-ai-document-title">
                    <strong>{item.title}</strong>
                    <span className={`local-ai-document-scope ${item.source_kind === 'upload' ? 'is-private' : 'is-shared'}`}>
                      {item.source_kind === 'upload' ? uiText("Личный") : uiText("Общий")}
                    </span>
                  </div>
                  <span className={`is-${item.status}`}>
                    {statusLabel(item.status)}
                    {['pending', 'indexing'].includes(item.status) ? ` · ${item.progress || 0}%` : ''}
                    {item.chunks_count ? uiText(" · {v0} фрагм.", {v0: (item.chunks_count)}) : ''}
                  </span>
                  {['pending', 'indexing'].includes(item.status) ? (
                    <div
                      className="local-ai-index-progress"
                      role="progressbar"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-valuenow={item.progress || 0}
                    >
                      <span style={{ width: `${item.progress || 0}%` }} />
                    </div>
                  ) : null}
                  {item.index_stage ? <small className="local-ai-index-stage">{item.index_stage}</small> : null}
                  {item.error_text ? <small>{item.error_text}</small> : null}
                </div>
                {item.source_kind === 'upload' && ['pending', 'indexing'].includes(item.status) ? (
                  <button
                    type="button"
                    className="local-ai-icon-button is-cancel"
                    title={uiText("Отменить индексацию")}
                    aria-label={uiText("Отменить индексацию файла {v0}", {v0: (item.title)})}
                    onClick={() => cancelDocument(item.id)}
                    disabled={item.cancel_requested}
                  >
                    <XCircle size={15} />
                  </button>
                ) : item.source_kind === 'upload' ? (
                  <button type="button" className="local-ai-icon-button" title={uiText("Удалить документ")} onClick={() => deleteDocument(item.id)}>
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))}
            {!documents.length ? <div className="local-ai-empty-small">{uiText("Добавьте документы или запустите индексацию аудитов")}</div> : null}
          </div>
        </aside>
      </div>
    </div>
  );
}
